// Centralized lead webhook for ALL frayze.ca forms.
//
// Why this exists: GoHighLevel's inbound webhook trigger locks its field mapping
// from the payloads it sees. If a field is sometimes missing, the GHL mapping for
// it becomes unreliable and downstream automations break. So EVERY form must POST
// the EXACT SAME flat set of keys, every time, with "" for anything not relevant.
//
// Each form supplies only the fields it has + a unique `source`. This helper merges
// those over the full canonical schema, flattens everything to strings, and sends
// the complete payload to the single webhook below.

import { getUTMData } from './utm';

export const LEAD_WEBHOOK_URL =
  'https://services.leadconnectorhq.com/hooks/9A5cW9ju0zoGXbGIDmyW/webhook-trigger/e1fdfc92-b82b-46e0-8706-dc5e46fb2e93';

// The canonical schema. ORDER + COMPLETENESS matter: every key is always sent.
// Add a new field here (and only here) when a form needs to capture something new.
export const LEAD_FIELDS = [
  //: identity
  'first_name',
  'last_name',
  'name',
  'email',
  'phone',
  'company',
  'website',
  //: qualifiers (segment / route on these)
  'source',
  'form_location',
  'industry',
  'business_type',
  'employees',
  'has_website',
  'budget',
  'interested_solution',
  'message',
  // `details` holds any extra / verbose context (calculator numbers, audit scores,
  // quiz answers) as one readable text blob: so they don't explode into 30+ columns.
  'details',
  //: consent (CASL)
  'consent_response',
  'consent_marketing',
  //: attribution / meta
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'referrer',
  'page_location',
] as const;

export type LeadField = (typeof LEAD_FIELDS)[number];
export type LeadInput = Partial<Record<LeadField, unknown>>;

// Coerce any value to a flat string. Objects/arrays are JSON-encoded so the payload
// never contains nested structures (GHL inbound webhooks want flat string values).
function flat(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

/**
 * Build the complete, flat, every-key-present payload for a form.
 * Exposed for tests/preview; most callers should use submitLead().
 */
export function buildLeadPayload(source: string, input: LeadInput): Record<LeadField, string> {
  const utm = getUTMData() as Record<string, unknown>;

  // Auto-filled meta: a form can still override any of these by passing them in.
  const auto: LeadInput = {
    source,
    page_location: typeof window !== 'undefined' ? window.location.href : '',
    referrer: (utm.referrer as string) || '',
    utm_source: (utm.utm_source as string) || '',
    utm_medium: (utm.utm_medium as string) || '',
    utm_campaign: (utm.utm_campaign as string) || '',
    utm_term: (utm.utm_term as string) || '',
    utm_content: (utm.utm_content as string) || '',
  };

  const merged: LeadInput = { ...auto, ...input, source };

  // Emit EXACTLY the canonical keys, in order, all as strings: nothing more, nothing less.
  const payload = {} as Record<LeadField, string>;
  for (const key of LEAD_FIELDS) {
    payload[key] = flat(merged[key]);
  }
  return payload;
}

/**
 * POST a lead to the single Frayze webhook with the full canonical schema.
 *
 * @param source  Unique per-form identifier (e.g. 'contact_page', 'savings_calculator').
 * @param input   Only the fields this form has; everything else is filled with "".
 * @param opts    `conversion: false` skips the Pixel/GA4 lead events (vendor pitches are not leads).
 * @returns       The fetch Response (callers can check response.ok).
 */
export async function submitLead(
  source: string,
  input: LeadInput,
  opts: { conversion?: boolean } = {},
): Promise<Response> {
  const payload = buildLeadPayload(source, input);
  const res = await fetch(LEAD_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  // Fire the Meta Pixel "Lead" conversion (and GA4 generate_lead) on success: every
  // lead form routes through here, so this is the single source of conversion truth.
  if (res.ok && opts.conversion !== false && typeof window !== 'undefined') {
    try {
      const w = window as unknown as { fbq?: (...a: unknown[]) => void; gtag?: (...a: unknown[]) => void };
      w.fbq?.('track', 'Lead', { content_name: source });
      w.gtag?.('event', 'generate_lead', { lead_source: source });
    } catch { /* analytics must never break a submit */ }
  }
  return res;
}
